/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React from 'react';
import { ICON_PERSON } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { Tooltip, TOOLTIP_POSITION_TOP, TooltipProps } from '../Tooltip';
import { avatarDefaults, AvatarProps } from './Avatar.props';

/**
 * SDS Avatar component.
 */
export const Avatar = (props: AvatarProps): JSX.Element => {
  const { avatarSize, classes, isInteractive, showTooltip, user, ...rest } = {
    ...avatarDefaults,
    ...props,
  };

  const avatarClass = clsx(`sds-avatar`, `size--${avatarSize}`, classes, {
    [`sds-avatar-photo`]: user.photo,
  });

  // Helper function to get the initial of a word
  const getInitial = (text: string): string => text.substring(0, 1).toUpperCase();

  const { email, firstName, lastName } = user;

  const userName = user.firstName !== `` ? `${user.firstName} ${user.lastName}` : user.email;
  let userInitial = getInitial(firstName) + getInitial(lastName);
  if (!userInitial) {
    userInitial = getInitial(email);
  }

  let avatarContent;

  const tooltipProps: TooltipProps = {
    label: userName,
    position: TOOLTIP_POSITION_TOP,
  };

  if (userInitial) {
    avatarContent = <span className="sds-avatar-initial">{userInitial}</span>;
  } else {
    avatarContent = (
      <div className="sds-avatar-icon">
        <SdsIcon svg={ICON_PERSON} />
      </div>
    );
  }

  let userAvatar = (
    <div className={avatarClass} {...rest}>
      {avatarContent}
    </div>
  );

  if (user.photo) {
    userAvatar = (
      <div className={avatarClass} style={{ backgroundImage: `url(${user.photo})` }} {...rest}>
        <span className="sr-only">{userName}</span>
      </div>
    );
  }

  if (isInteractive) {
    userAvatar = <button className="sds-avatar-button">{userAvatar}</button>;
  }

  if (userInitial && showTooltip) {
    return <Tooltip {...tooltipProps}>{userAvatar}</Tooltip>;
  }

  return userAvatar;
};
